import { createStaticMapsUrl } from '@vis.gl/react-google-maps'
import config from 'src/config'
import { ICoordinates } from 'src/types/graphql'
import { MAP_IDS } from './mapIds'

// Google fetches marker icons server-side, so the icon must be an absolute,
// globally-reachable URL — an env-relative path would be unreachable from
// localhost. This is the canonical public brand pin, identical across envs.
//
// It MUST be the 64px variant: Static Maps caps custom icons at 4096 pixels
// TOTAL (64x64 square) and silently falls back to its default red teardrop
// when the icon is bigger — which is exactly what the full-size
// `map_icon.png` (1080x1080) did on every static preview until 2026-08-23.
// If you ever repoint this, keep it <= 64x64.
const PIN_ICON = 'https://www.spotme.mx/images/map_pin_static.png'

// 640 is the max width for the standard Static Maps tier; scale:2 renders it
// crisp on the high-DPR mobile devices that are ~90% of our traffic. The
// preview is shown behind `bg-cover`, so exact aspect ratio doesn't matter.
const DEFAULT_WIDTH = 640
const DEFAULT_HEIGHT = 400
const DEFAULT_ZOOM = 14

type StaticMapUrlArgs = {
  center: ICoordinates
  zoom?: number
  /** Use the clean "white" cloud style (matches the interactive map's `white`). */
  white?: boolean
  width?: number
  height?: number
  /**
   * Draw the brand pin at `center`. Only true when the centre point IS the
   * subject (a directory facility). On a search/city/hood map the centre is
   * just the viewport centroid — a pin there marks nothing, and the
   * interactive map that replaces the preview doesn't draw one either.
   */
  marker?: boolean
}

/**
 * Builds a Google Static Maps API image URL for the lazy map preview.
 *
 * Returns `null` when the Static Maps key isn't configured (e.g. an env that
 * hasn't been provisioned yet), so callers fall back to the neutral gray
 * placeholder — no broken image, no behavior change. The returned string is a
 * pure function of the (already-obfuscated) center + zoom, so it's identical
 * on server and client and safe to bake into SSR HTML.
 */
export function getStaticMapUrl({
  center,
  zoom = DEFAULT_ZOOM,
  white = false,
  width = DEFAULT_WIDTH,
  height = DEFAULT_HEIGHT,
  marker = true
}: StaticMapUrlArgs): string | null {
  const apiKey = config.googleMapsStaticKey
  if (!apiKey || !center) return null

  const location = { lat: center.lat, lng: center.lng }

  return createStaticMapsUrl({
    apiKey,
    width,
    height,
    scale: 2,
    center: location,
    zoom,
    mapId: white ? MAP_IDS.white : MAP_IDS.retail,
    markers: marker ? [{ location, icon: PIN_ICON }] : []
  })
}
